import { useCallback, useRef, useState, type DragEvent, type TouchEvent } from 'react';

type UseReorderableRowsOptions = {
  disabled?: boolean;
  onReorder: (fromIndex: number, toIndex: number) => void;
};

function findRowIndexAtPoint(x: number, y: number): number | null {
  const element = document.elementFromPoint(x, y);
  const row = element?.closest('[data-reorder-index]');
  if (!row) {
    return null;
  }

  const rowElement = row as HTMLElement;
  const value = rowElement.dataset.reorderIndex;
  if (value == null || value === '') {
    return null;
  }

  const index = Number(value);
  return Number.isFinite(index) ? index : null;
}

export function useReorderableRows({ disabled = false, onReorder }: UseReorderableRowsOptions) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragFromRef = useRef<number | null>(null);

  const clearDrag = useCallback(() => {
    dragFromRef.current = null;
    setDragIndex(null);
  }, []);

  const finishDrag = useCallback(
    (fromIndex: number, x: number, y: number) => {
      const toIndex = findRowIndexAtPoint(x, y);
      clearDrag();

      if (toIndex != null && toIndex !== fromIndex) {
        onReorder(fromIndex, toIndex);
      }
    },
    [clearDrag, onReorder]
  );

  const startTouchDrag = useCallback(
    (index: number, event: TouchEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }

      event.stopPropagation();
      dragFromRef.current = index;
      setDragIndex(index);

      const onTouchMove = (touchEvent: globalThis.TouchEvent) => {
        const touch = touchEvent.touches[0];
        if (!touch) {
          return;
        }
        touchEvent.preventDefault();
      };

      const onTouchEnd = (touchEvent: globalThis.TouchEvent) => {
        const touch = touchEvent.changedTouches[0];
        if (touch && dragFromRef.current != null) {
          finishDrag(dragFromRef.current, touch.clientX, touch.clientY);
        } else {
          clearDrag();
        }
        cleanup();
      };

      const cleanup = () => {
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
        document.removeEventListener('touchcancel', onTouchEnd);
      };

      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd);
      document.addEventListener('touchcancel', onTouchEnd);
    },
    [clearDrag, disabled, finishDrag]
  );

  const getRowProps = useCallback(
    (index: number) => ({
      'data-reorder-index': index,
      draggable: !disabled,
      onDragStart: (event: DragEvent<HTMLTableRowElement>) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        setDragIndex(index);
      },
      onDragOver: (event: DragEvent<HTMLTableRowElement>) => event.preventDefault(),
      onDrop: (event: DragEvent<HTMLTableRowElement>) => {
        event.preventDefault();
        if (dragIndex == null || dragIndex === index) {
          clearDrag();
          return;
        }
        onReorder(dragIndex, index);
        clearDrag();
      },
      onDragEnd: clearDrag,
    }),
    [clearDrag, disabled, dragIndex, onReorder]
  );

  const getHandleProps = useCallback(
    (index: number) => ({
      onTouchStart: (event: TouchEvent<HTMLElement>) => startTouchDrag(index, event),
    }),
    [startTouchDrag]
  );

  return {
    dragIndex,
    getRowProps,
    getHandleProps,
  };
}
